import React, { Dispatch, SetStateAction, useEffect } from 'react'
import { Controller, FieldErrors, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import DateOfBirth, {
  DateErrorType,
  DateType,
} from '@node-space/storybook-components/dist/DateOfBirth'
import { InputLabel } from '@node-space/storybook-components/dist/FormElements'
import Input from '@node-space/storybook-components/dist/Input'
import Select from '@node-space/storybook-components/dist/Select'
import { Box, Box as GroupFields } from '@node-space/ui-components'
import { AddressFormFields } from 'components/AddressFormFields/AddressFormFields'
import { getAddressFields } from 'components/AddressFormFields/utils'
import { InputLabelOptionalTag } from 'components/Text/InputLabelOptionalTag'
import { InputLabelTooltip } from 'components/Text/InputLabelTooltip'
import { useCountriesContext } from 'contexts/countriesContext'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useVerificationContext } from 'hooks/context/useVerificationContext'
import { useDateOfBirthInput } from 'hooks/useDateOfBirthInput'
import { AccountContactSummary } from './AccountContactSummary'
import { ControllerFields } from './ControllerFields'
import { AccountContactTypes } from './helpers/constants'
import {
  AccountContactData,
  AccountContactFormData,
  AccountContactFormKey,
  AccountContactType,
} from './helpers/types'
import { getAccountContactName, getIsPersonMyself, getPersonDisplayTypes } from './helpers/utils'

export interface AccountContactFieldsProps {
  contactType: AccountContactType
  savedData: AccountContactData
  form: UseFormReturn<AccountContactFormData, any, undefined>
  formFields: Record<AccountContactFormKey, any>
  formValues: Partial<Record<AccountContactFormKey, string>>
  formErrors?: FieldErrors<AccountContactFormData>
  isEditingContact?: boolean
  setIsDateOfBirthError: Dispatch<SetStateAction<boolean>>
}

export const AccountContactFields = ({
  contactType,
  savedData,
  form,
  formFields,
  formValues,
  formErrors,
  isEditingContact,
  setIsDateOfBirthError,
}: AccountContactFieldsProps) => {
  const { t } = useTranslation()
  const { profile } = useProfileContext()
  const { accountContacts } = useVerificationContext()
  const { enableDualNationality } = useFeatureFlags()
  const { isLoading: isCountriesLoading } = useCountriesContext()

  const {
    dateOfBirthLabels,
    hasDateOfBirthErrors,
    getDateOfBirthFields,
    setDateOfBirthInputValue,
    setDateOfBirthErrors,
    getDateOfBirthErrors,
    getDateOfBirthErrorText,
  } = useDateOfBirthInput()

  const addressFields = getAddressFields(formFields)

  const { person } = savedData || {}
  const personReference = savedData?.person?.reference
  const contactTypes = !!personReference
    ? accountContacts?.list
        ?.filter(otherContact => otherContact?.person?.reference === personReference)
        ?.map(({ type }) => type)
    : []

  const isAccountRepType = contactType === AccountContactTypes.ACCOUNT_REPRESENTATIVE
  const isMyself = getIsPersonMyself(person, profile)
  const isAccountRepMyself = isAccountRepType && isMyself
  const isOtherContactType = !!contactTypes?.filter(type => type !== contactType)?.length

  const showContactSummary = isMyself || (isOtherContactType && !isEditingContact)
  const showFullForm = isAccountRepMyself || !isOtherContactType || isEditingContact
  const showEmailField = isAccountRepType && !isMyself
  const showNationalityField = showFullForm || !person?.nationalityCountryCode
  const showPersonalIdentificationNumber = showFullForm || !person?.personalIdentificationNumber
  const showControllerFields = !!formFields?.isController

  const contactFullName = getAccountContactName(savedData)
  const contactDetail = isAccountRepMyself
    ? savedData?.contactInfo?.emailAddress
    : getPersonDisplayTypes(contactTypes, t)

  useEffect(() => {
    setIsDateOfBirthError(hasDateOfBirthErrors)
  }, [hasDateOfBirthErrors])

  useEffect(() => {
    if (formValues?.nationalityCountryCode && formValues?.personalIdentificationNumber) {
      form?.trigger('personalIdentificationNumber')
    }
  }, [formValues?.nationalityCountryCode, formValues?.personalIdentificationNumber])

  return (
    <>
      {showContactSummary ? (
        <AccountContactSummary
          contactFullName={contactFullName}
          contactDetail={contactDetail ?? t('accountContact.typesLabel.none')}
        />
      ) : (
        <Box>
          <InputLabel forId={formFields?.firstName?.name}>
            {t('verification.businessOwners.legalNameOfPerson')}
          </InputLabel>
          <GroupFields flexCol gapY={8}>
            <Input {...formFields?.firstName} />
            <Input {...formFields?.lastName} />
          </GroupFields>
        </Box>
      )}
      {showEmailField && <Input {...formFields?.emailAddress} />}
      {showFullForm && (
        <Controller
          control={form?.control}
          name="dateOfBirth"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <DateOfBirth
              date={getDateOfBirthFields(value)}
              labels={{
                ...dateOfBirthLabels,
                errorMessage: getDateOfBirthErrorText(error),
              }}
              errors={getDateOfBirthErrors(error)}
              onChange={(date: DateType) => onChange(setDateOfBirthInputValue(date))}
              onValidate={(errors: DateErrorType) => setDateOfBirthErrors(errors)}
            />
          )}
        />
      )}
      {showNationalityField && (
        <Select {...formFields?.nationalityCountryCode} loading={isCountriesLoading} isSearchable />
      )}
      {enableDualNationality && showFullForm && (
        <Box>
          <InputLabel forId={formFields?.secondNationalityCountryCode?.name}>
            {formFields?.secondNationalityCountryCode?.label} <InputLabelOptionalTag />
          </InputLabel>
          <Select
            {...formFields?.secondNationalityCountryCode}
            loading={isCountriesLoading}
            label=""
            isSearchable
            onChange={value => {
              const isAlreadySelected = value === formValues?.secondNationalityCountryCode
              const updatedValue = isAlreadySelected ? '' : value
              form?.setValue('secondNationalityCountryCode', updatedValue, { shouldDirty: true })
            }}
          />
        </Box>
      )}
      {showPersonalIdentificationNumber && (
        <Box>
          <InputLabelTooltip
            forId={formFields?.personalIdentificationNumber?.name}
            tooltipText={t('formFields.personalIdentificationNumberTooltip')}
          >
            {formFields?.personalIdentificationNumber?.label}
          </InputLabelTooltip>
          <Input {...formFields?.personalIdentificationNumber} label="" />
        </Box>
      )}
      {showFullForm && (
        <AddressFormFields addressFields={addressFields} isLoadingCountries={isCountriesLoading} />
      )}
      {showControllerFields && <ControllerFields formFields={formFields} formErrors={formErrors} />}
    </>
  )
}
